import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useHideCursor } from '@/hooks/useIdleDetection';
import DiegeticTimer from './DiegeticTimer';
import TaskSurface from './TaskSurface';
import BreathPill from './BreathPill';
import confetti from '@/confetti';

interface YouTubePlayer {
  cueVideoById: (id: string, startSeconds?: number, quality?: string) => void;
  playVideo: () => void;
  pauseVideo: () => void;
  stopVideo: () => void;
  setVolume: (volume: number) => void;
}

interface WorldProps {
  session: ReturnType<typeof import('@/hooks/useSession').useSession>;
  isExiting?: boolean;
  player?: YouTubePlayer | null;
  othersFocusing?: number;
  notice?: string; // brief arrival/departure line, replaces the count while shown
  noticeId?: number;
  chimeOn?: boolean;
  onChimeToggle?: () => void;
}

// Controls stay out of sight for this long after arriving, to let the world land
const ARRIVAL_QUIET_MS = 3000;

// The session video itself lives on the persistent stage in App; this is only
// the in-world layer on top of it.
const World: React.FC<WorldProps> = ({
  session,
  isExiting = false,
  player,
  othersFocusing = 0,
  notice,
  noticeId,
  chimeOn,
  onChimeToggle,
}) => {
  const [arrived, setArrived] = useState(false);
  const [volume, setVolume] = useState(50);
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTaskText, setNewTaskText] = useState('');
  const cursorHidden = useHideCursor(session.isRunning && !session.isPaused, 2000);
  const prevCompletedCountRef = useRef(session.completedTaskCount);

  // Celebrate when a task is completed
  useEffect(() => {
    if (session.completedTaskCount > prevCompletedCountRef.current) {
      confetti.start(1500, 30, 60);
    }
    prevCompletedCountRef.current = session.completedTaskCount;
  }, [session.completedTaskCount]);

  // Sync music with pause state
  useEffect(() => {
    if (!player) return;

    if (session.isPaused) {
      player.pauseVideo();
    } else if (session.isRunning) {
      player.playVideo();
    }
  }, [session.isPaused, session.isRunning, player]);

  // Handle volume changes
  useEffect(() => {
    if (player) {
      player.setVolume(volume);
    }
  }, [volume, player]);

  // Stop music on exit
  useEffect(() => {
    if (isExiting && player) {
      player.pauseVideo();
    }
  }, [isExiting, player]);

  useEffect(() => {
    const timer = setTimeout(() => setArrived(true), ARRIVAL_QUIET_MS);
    return () => clearTimeout(timer);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {
        // Fullscreen not supported or denied
      });
    } else {
      document.exitFullscreen();
    }
  }, []);

  const cancelAddTask = useCallback(() => {
    setShowAddTask(false);
    setNewTaskText('');
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle if typing in an input (except Escape)
      if (
        (e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement) &&
        e.key !== 'Escape'
      ) {
        return;
      }

      switch (e.key.toLowerCase()) {
        case ' ':
          e.preventDefault();
          session.togglePause();
          break;
        case 't':
          e.preventDefault();
          // Complete current task and advance to next
          if (session.currentTask && !session.currentTask.completed) {
            session.completeTask();
          }
          break;
        case 'a':
          e.preventDefault();
          setShowAddTask(true);
          break;
        case 'f':
          e.preventDefault();
          toggleFullscreen();
          break;
        case 'escape':
          e.preventDefault();
          if (showAddTask) {
            cancelAddTask();
          } else if (document.fullscreenElement) {
            document.exitFullscreen();
          } else {
            session.exit();
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [session, showAddTask, toggleFullscreen, cancelAddTask]);

  const handleAddTask = useCallback(() => {
    if (newTaskText.trim()) {
      session.addTask(newTaskText.trim());
      cancelAddTask();
    }
  }, [session, newTaskText, cancelAddTask]);

  const controlsVisible =
    arrived && (!cursorHidden || session.isPaused || showAddTask);
  const totalSeconds = session.context.duration * 60;
  const progress = totalSeconds > 0 ? 1 - session.context.remaining / totalSeconds : 0;

  return (
    <div
      className={`th-world ${isExiting ? 'is-exiting' : ''} ${
        session.isPaused ? 'is-paused' : ''
      }`}
    >
      {notice ? (
        <p key={noticeId} className="th-presence is-notice" aria-live="polite">
          {notice}
        </p>
      ) : (
        othersFocusing > 0 && (
          <p key="count" className="th-presence">
            you + {othersFocusing} {othersFocusing === 1 ? 'other' : 'others'}
          </p>
        )
      )}

      <div className="th-world-center">
        <p className="th-break" aria-live="polite">
          {session.isPaused ? 'break — stand up. water. back in.' : ''}
        </p>

        <DiegeticTimer remaining={session.context.remaining} paused={session.isPaused} />

        <div className="th-world-task">
          {showAddTask ? (
            <input
              type="text"
              value={newTaskText}
              onChange={(e) => setNewTaskText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddTask();
              }}
              onBlur={() => {
                if (!newTaskText.trim()) cancelAddTask();
              }}
              placeholder="what's next?"
              className="th-task-input"
              aria-label="New task"
              autoFocus
            />
          ) : session.currentTask ? (
            <TaskSurface
              task={session.currentTask.text}
              completed={session.currentTask.completed}
              onToggle={session.completeTask}
              taskIndicator={
                session.totalTaskCount > 1
                  ? `${session.context.currentTaskIndex + 1}/${session.totalTaskCount}`
                  : undefined
              }
            />
          ) : (
            <button
              onClick={() => setShowAddTask(true)}
              className={`th-task-add ${controlsVisible ? '' : 'is-hidden'}`}
            >
              + name what you're working on
            </button>
          )}
        </div>
      </div>

      <BreathPill
        paused={session.isPaused}
        onPauseToggle={session.togglePause}
        onExit={session.exit}
        onFullscreen={toggleFullscreen}
        taskCompleted={session.currentTask?.completed ?? false}
        onTaskToggle={session.completeTask}
        hidden={!controlsVisible}
        volume={volume}
        onVolumeChange={player ? setVolume : undefined}
        onAddTask={() => setShowAddTask(true)}
        hasTask={!!session.currentTask}
        chimeOn={chimeOn}
        onChimeToggle={onChimeToggle}
      />

      <div
        className="th-progress"
        style={{ transform: `scaleX(${progress})` }}
        aria-hidden="true"
      />
    </div>
  );
};

export default World;
